import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSharedQuotes, useDeleteSharedQuote } from '@/hooks/useSharedQuotes';
import { useToast } from '@/contexts/ToastContext';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { StatusFilter } from '@/components/admin/StatusFilter';

const SHARED_QUOTE_STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'viewed', label: 'Viewed' },
  { value: 'ordered', label: 'Ordered' },
  { value: 'expired', label: 'Expired' },
] as const;

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  draft: { bg: 'var(--bg-surface-sunken)', text: 'var(--text-tertiary)' },
  sent: { bg: 'var(--color-primary-50)', text: 'var(--color-primary-700)' },
  viewed: { bg: '#fef3c7', text: '#92400e' },
  ordered: { bg: 'var(--color-success-50)', text: 'var(--color-success-600)' },
  expired: { bg: 'var(--bg-surface-sunken)', text: 'var(--text-tertiary)' },
};

const copyShareLink = async (shareToken: string): Promise<boolean> => {
  const url = `${window.location.origin}/q/${shareToken}`;
  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch {
    return false;
  }
};

export const AdminSharedQuotesPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState('');
  const [deletingToken, setDeletingToken] = useState<string | null>(null);

  const { data, isLoading, error } = useSharedQuotes(statusFilter || undefined);
  const deleteSharedQuote = useDeleteSharedQuote();

  const handleCopyLink = async (shareToken: string) => {
    const success = await copyShareLink(shareToken);
    if (success) {
      toast.success('Link copied to clipboard');
    } else {
      toast.error('Failed to copy link');
    }
  };

  const handleDelete = async (shareToken: string) => {
    if (deletingToken) return;
    const confirmed = window.confirm('Delete this shared quote? This cannot be undone.');
    if (!confirmed) return;

    setDeletingToken(shareToken);
    try {
      await deleteSharedQuote.mutateAsync(shareToken);
      toast.success('Shared quote deleted');
    } catch {
      toast.error('Failed to delete shared quote');
    } finally {
      setDeletingToken(null);
    }
  };

  return (
    <div>
      <div
        className="flex items-center justify-between"
        style={{ marginBottom: 'var(--space-6)' }}
      >
        <h1
          style={{
            fontSize: 'var(--text-2xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--text-primary)',
            letterSpacing: 'var(--tracking-tight)',
            margin: 0,
          }}
        >
          Shared Quotes
        </h1>
        <button
          type="button"
          onClick={() => navigate('/admin/shared-quotes/new')}
          style={{
            padding: 'var(--space-2) var(--space-4)',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'white',
            backgroundColor: 'var(--color-primary-600)',
            border: 'none',
            borderRadius: 'var(--radius-lg)',
            cursor: 'pointer',
            transition: 'background-color var(--duration-fast) var(--ease-out)',
          }}
        >
          New Quote
        </button>
      </div>

      <div style={{ marginBottom: 'var(--space-6)' }}>
        <StatusFilter
          value={statusFilter}
          onChange={setStatusFilter}
          options={SHARED_QUOTE_STATUS_OPTIONS}
        />
      </div>

      {isLoading && (
        <div style={{ paddingTop: 'var(--space-12)', paddingBottom: 'var(--space-12)' }}>
          <LoadingSpinner size="lg" />
        </div>
      )}

      {error && (
        <div
          className="text-center"
          style={{
            padding: 'var(--space-8)',
            color: 'var(--color-error-500)',
            fontSize: 'var(--text-sm)',
          }}
        >
          Failed to load shared quotes. Please try again.
        </div>
      )}

      {data && data.data.length === 0 && (
        <div
          className="text-center"
          style={{
            padding: 'var(--space-12)',
            color: 'var(--text-tertiary)',
            fontSize: 'var(--text-sm)',
          }}
        >
          No shared quotes found
        </div>
      )}

      {data && data.data.length > 0 && (
        <>
          {/* Mobile card list */}
          <div className="mobile-card-list">
            {data.data.map((quote) => {
              const colors = STATUS_COLORS[quote.status] ?? STATUS_COLORS.expired;
              return (
                <div
                  key={quote.shareToken}
                  style={{
                    borderRadius: 'var(--radius-lg)',
                    border: '1px solid var(--border-default)',
                    backgroundColor: 'var(--bg-surface)',
                    padding: 'var(--space-3) var(--space-4)',
                  }}
                >
                  <div
                    className="flex items-center justify-between"
                    style={{ marginBottom: 'var(--space-2)' }}
                  >
                    <span
                      style={{
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-weight-medium)',
                        color: 'var(--text-primary)',
                        fontFamily: 'monospace',
                      }}
                    >
                      {quote.shareToken.slice(-8).toUpperCase()}
                    </span>
                    <span
                      style={{
                        borderRadius: 'var(--radius-full)',
                        padding: 'var(--space-1) var(--space-3)',
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-weight-medium)',
                        backgroundColor: colors.bg,
                        color: colors.text,
                        textTransform: 'capitalize',
                      }}
                    >
                      {quote.status}
                    </span>
                  </div>
                  <p
                    style={{
                      fontSize: 'var(--text-sm)',
                      color: 'var(--text-secondary)',
                      margin: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {quote.title}
                  </p>
                  {quote.customerName && (
                    <p
                      style={{
                        fontSize: 'var(--text-xs)',
                        color: 'var(--text-tertiary)',
                        margin: 0,
                        marginTop: 'var(--space-1)',
                      }}
                    >
                      {quote.customerName}
                    </p>
                  )}
                  <div
                    className="flex items-center justify-between"
                    style={{ marginTop: 'var(--space-2)' }}
                  >
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                      {new Date(quote.createdAt).toLocaleDateString()}
                    </span>
                    <div className="flex items-center" style={{ gap: 'var(--space-2)' }}>
                      <button
                        type="button"
                        onClick={() => handleCopyLink(quote.shareToken)}
                        style={{
                          padding: 'var(--space-1) var(--space-2)',
                          fontSize: 'var(--text-xs)',
                          fontWeight: 'var(--font-weight-medium)',
                          color: 'var(--color-primary-600)',
                          backgroundColor: 'var(--color-primary-50)',
                          border: '1px solid var(--color-primary-200)',
                          borderRadius: 'var(--radius-md)',
                          cursor: 'pointer',
                        }}
                      >
                        Copy Link
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(quote.shareToken)}
                        disabled={deletingToken === quote.shareToken}
                        style={{
                          padding: 'var(--space-1) var(--space-2)',
                          fontSize: 'var(--text-xs)',
                          fontWeight: 'var(--font-weight-medium)',
                          color: 'var(--color-error-500)',
                          backgroundColor: 'transparent',
                          border: '1px solid var(--color-error-200)',
                          borderRadius: 'var(--radius-md)',
                          cursor: deletingToken === quote.shareToken ? 'not-allowed' : 'pointer',
                          opacity: deletingToken === quote.shareToken ? 0.5 : 1,
                        }}
                      >
                        {deletingToken === quote.shareToken ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop table */}
          <div
            className="desktop-table overflow-hidden"
            style={{
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--border-default)',
              backgroundColor: 'var(--bg-surface)',
            }}
          >
            <div className="table-scroll">
              <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '700px' }}>
                <thead style={{ backgroundColor: 'var(--bg-surface-sunken)' }}>
                  <tr>
                    {['Token', 'Title', 'Customer', 'Status', 'Created', 'Actions'].map(
                      (heading) => (
                        <th
                          key={heading}
                          style={{
                            padding: 'var(--space-3) var(--space-4)',
                            textAlign: 'left',
                            fontSize: 'var(--text-xs)',
                            fontWeight: 'var(--font-weight-medium)',
                            textTransform: 'uppercase',
                            letterSpacing: 'var(--tracking-wide)',
                            color: 'var(--text-tertiary)',
                            borderBottom: '1px solid var(--border-default)',
                          }}
                        >
                          {heading}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((quote) => {
                    const colors = STATUS_COLORS[quote.status] ?? STATUS_COLORS.expired;
                    return (
                      <tr
                        key={quote.shareToken}
                        style={{
                          borderBottom: '1px solid var(--border-subtle)',
                          transition: 'background-color var(--duration-fast) var(--ease-out)',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--bg-surface-sunken)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <td
                          style={{
                            padding: 'var(--space-3) var(--space-4)',
                            fontSize: 'var(--text-sm)',
                            fontWeight: 'var(--font-weight-medium)',
                            color: 'var(--text-primary)',
                            fontFamily: 'monospace',
                          }}
                        >
                          {quote.shareToken.slice(-8).toUpperCase()}
                        </td>
                        <td
                          style={{
                            padding: 'var(--space-3) var(--space-4)',
                            fontSize: 'var(--text-sm)',
                            color: 'var(--text-secondary)',
                            maxWidth: '200px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {quote.title}
                        </td>
                        <td
                          style={{
                            padding: 'var(--space-3) var(--space-4)',
                            fontSize: 'var(--text-sm)',
                            color: 'var(--text-secondary)',
                          }}
                        >
                          {quote.customerName ?? '--'}
                        </td>
                        <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                          <span
                            style={{
                              display: 'inline-block',
                              borderRadius: 'var(--radius-full)',
                              padding: 'var(--space-1) var(--space-3)',
                              fontSize: 'var(--text-xs)',
                              fontWeight: 'var(--font-weight-medium)',
                              backgroundColor: colors.bg,
                              color: colors.text,
                              textTransform: 'capitalize',
                            }}
                          >
                            {quote.status}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: 'var(--space-3) var(--space-4)',
                            fontSize: 'var(--text-sm)',
                            color: 'var(--text-tertiary)',
                          }}
                        >
                          {new Date(quote.createdAt).toLocaleDateString()}
                        </td>
                        <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                          <div className="flex items-center" style={{ gap: 'var(--space-2)' }}>
                            <button
                              type="button"
                              onClick={() => handleCopyLink(quote.shareToken)}
                              style={{
                                padding: 'var(--space-1) var(--space-2)',
                                fontSize: 'var(--text-xs)',
                                fontWeight: 'var(--font-weight-medium)',
                                color: 'var(--color-primary-600)',
                                backgroundColor: 'var(--color-primary-50)',
                                border: '1px solid var(--color-primary-200)',
                                borderRadius: 'var(--radius-md)',
                                cursor: 'pointer',
                              }}
                            >
                              Copy Link
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(quote.shareToken)}
                              disabled={deletingToken === quote.shareToken}
                              style={{
                                padding: 'var(--space-1) var(--space-2)',
                                fontSize: 'var(--text-xs)',
                                fontWeight: 'var(--font-weight-medium)',
                                color: 'var(--color-error-500)',
                                backgroundColor: 'transparent',
                                border: '1px solid var(--color-error-200)',
                                borderRadius: 'var(--radius-md)',
                                cursor:
                                  deletingToken === quote.shareToken ? 'not-allowed' : 'pointer',
                                opacity: deletingToken === quote.shareToken ? 0.5 : 1,
                              }}
                            >
                              {deletingToken === quote.shareToken ? 'Deleting...' : 'Delete'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
